import { TIME } from 'util/constants'

import {
  differenceInSeconds,
  format,
  formatDistanceToNow,
  formatDistanceToNowStrict,
  isFuture,
  isThisYear,
  isToday,
  parseISO,
} from 'date-fns'
import React, { useEffect, useRef, useState } from 'react'

// date-fns doesn't export unit typings
type DateFnsUnit = 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year'

// enum time styles
export enum ETS {
  'default' = 0,
  'twitter',
  'future',
  'day',
  'countdown',
}

// enum time units
export enum ETU {
  'SECOND' = 0,
  'MINUTE',
  'HOUR',
  'DAY',
  'SAME_YEAR',
  'DIFF_YEAR',
}

interface TimestampProps {
  // consider updating to regex for valid times, __maybe__
  date: string
  timeStyle?: ETS
  forceUnit?: ETU
  updateInterval?: number
  prefix?: string
  suffix?: string
}

export const Timestamp = ({
  date,
  timeStyle = ETS.default,
  forceUnit,
  prefix = '',
  suffix = '',
  ...props
}: TimestampProps & React.HTMLAttributes<HTMLElement>) => {
  const [, setTime] = useState(now())
  const intervalRef = useRef(0)

  useEffect(() => {
    let updateInterval

    switch (timeStyle) {
      case ETS.day:
        updateInterval = 3000000
        break
      case ETS.default:
        updateInterval = 60000
        break
      case ETS.future:
        if (intervalRef.current > 60) {
          updateInterval = 60000
        } else {
          updateInterval = 1000
        }
        break
      case ETS.twitter:
        if (intervalRef.current > 60) {
          updateInterval = 60000
        } else {
          updateInterval = 1000
        }
        break
    }

    const interval = setInterval(() => setTime(now()), updateInterval)

    return () => {
      clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalRef.current])

  return (
    <time
      dateTime={date}
      title={format(parseISO(date), timeTitleFormat)}
      {...props}
    >
      {`${prefix}${formatTimestamp(
        parseISO(date),
        now(),
        timeStyle,
        forceUnit,
        intervalRef,
      )}${suffix}`}
    </time>
  )
}

const timeTitleFormat = "EEEE, MMMM d, y, hh:mm:ss aaaaa'm'"

const now = () => Number(Date.now().toFixed(0))

const duration = (date: Date, unit: DateFnsUnit) =>
  formatDistanceToNowStrict(date, {
    unit: unit,
    roundingMethod: 'floor',
  })

const durationUnitless = (date: Date, unit: DateFnsUnit) =>
  duration(date, unit).split(new RegExp(` ${unit}`))[0]

// time formats are:

// - - - - - - - - - - - - - - - - - - DEFAULT
// SECOND       just now               (e.g. just now)
// MINUTE       N minutes ago          (e.g. 3 minutes ago)
// HOUR         N hours ago            (e.g. 4 hours ago)
// DAY          N days ago             (e.g. 5 days ago)
// SAME_YEAR    E, MMM d, h:m a/p      (e.g. Tue, 13 Oct, 5:41 pm)
// DIFF_YEAR    E, MMM d, y, h:m a/p   (e.g. Tue, 13 Oct, 2019, 5:41 pm)

// - - - - - - - - - - - - - - - - - - TWITTER
// SECOND       Ns                     (e.g. 2s)
// MINUTE       Nm                     (e.g. 3m)
// HOUR         Nh                     (e.g. 4h)
// DAY          MMM d                  (e.g. Jul 8)
// SAME_YEAR    same as DAY            (e.g. Jul 8)
// DIFF_YEAR    MMM d, y               (e.g. Jul 8, 1994)

// - - - - - - - - - - - - - - - - - - FUTURE
// SECOND       in N seconds           (e.g. 2 seconds)
// MINUTE       in N minutes           (e.g. 3 minutes)
// HOUR         in N hours             (e.g. 4 hours)
// DAY          E H a/p                (e.g. Mon 9 am)
// SAME_YEAR    d LLL                  (e.g. Jul 8)
// DIFF_YEAR    d LLL, y               (e.g. Jul 8, 1994)

interface HelperMethods {
  _H1: (date: Date) => string
  _H2: (date: Date) => string
}

type TimeFormats = {
  [key in ETS]: {
    [key in ETU]: (date: Date) => string
  }
}

const timeFormats: TimeFormats & HelperMethods = {
  // helper methods
  _H1: (date) => `${formatDistanceToNow(date)} ago`,
  _H2: (date) => format(date, 'MMM d'),
  // default
  [ETS.default]: {
    [ETU.SECOND]: () => 'just now',
    [ETU.MINUTE]: (date) => timeFormats._H1(date).replace(/about/g, ''),
    [ETU.HOUR]: (date) => timeFormats._H1(date).replace(/about/g, ''),
    [ETU.DAY]: (date) => timeFormats._H1(date),
    [ETU.SAME_YEAR]: (date) => format(date, "E, MMM d, hh:mm aaaaa'm'"),
    [ETU.DIFF_YEAR]: (date) => format(date, "E, MMM d, y, hh:mm aaaaa'm'"),
  },
  // twitter
  [ETS.twitter]: {
    [ETU.SECOND]: (date) => `${durationUnitless(date, 'second')}s`,
    [ETU.MINUTE]: (date) => `${durationUnitless(date, 'minute')}m`,
    [ETU.HOUR]: (date) => `${durationUnitless(date, 'hour')}h`,
    [ETU.DAY]: (date) => timeFormats._H2(date),
    [ETU.SAME_YEAR]: (date) => timeFormats._H2(date),
    [ETU.DIFF_YEAR]: (date) => format(date, 'MMM d, y'),
  },
  // future
  [ETS.future]: {
    [ETU.SECOND]: (date) => `in ${duration(date, 'second')}`,
    [ETU.MINUTE]: (date) => `in ${duration(date, 'minute')}`,
    [ETU.HOUR]: (date) => `in ${duration(date, 'hour')}`,
    [ETU.DAY]: (date) => format(date, "E h aaaaa'm'"),
    [ETU.SAME_YEAR]: (date) => format(date, 'd LLL'),
    [ETU.DIFF_YEAR]: (date) => format(date, 'd LLL, y'),
  },
  // day
  [ETS.day]: {
    [ETU.SECOND]: (date) => (isToday(date) ? 'Today' : 'Yesterday'),
    [ETU.MINUTE]: (date) => (isToday(date) ? 'Today' : 'Yesterday'),
    [ETU.HOUR]: (date) => (isToday(date) ? 'Today' : 'Yesterday'),
    [ETU.DAY]: (date) => format(date, 'LLLL do'),
    [ETU.SAME_YEAR]: (date) => format(date, 'LLLL do'),
    [ETU.DIFF_YEAR]: (date) => format(date, 'LLLL do, y'),
  },
  [ETS.countdown]: {
    [ETU.SECOND]: (date) => `${durationUnitless(date, 'second')} seconds`,
    [ETU.MINUTE]: (date) => `${durationUnitless(date, 'minute')} minutes`,
    [ETU.HOUR]: (date) => `${durationUnitless(date, 'hour')} hours`,
    [ETU.DAY]: (date) => timeFormats._H2(date),
    [ETU.SAME_YEAR]: (date) => timeFormats._H2(date),
    [ETU.DIFF_YEAR]: (date) => format(date, 'MMM d, y'),
  },
}

const formatTimestamp = (
  date: Date,
  now: number,
  style: ETS = ETS.default,
  forceUnit: ETU | false = false,
  ref: any,
) => {
  type TimePair = [key: Date | number, value: Date | number]
  const timePair: TimePair =
    style === ETS.countdown || isFuture(date)
      ? [date, now] // future
      : [now, date] // past (default)

  const secondsDiff = differenceInSeconds(...timePair)
  ref.current = secondsDiff

  if (style === ETS.countdown && secondsDiff < 0) {
    return '0 seconds'
  }

  // if forcing a unit, return early
  if (forceUnit !== false) {
    return timeFormats[style][forceUnit](date)
  }

  // under a canonical minute (so display seconds)
  if (secondsDiff < TIME.MINUTE) {
    return timeFormats[style][ETU.SECOND](date)
  }

  // under an hour (so display minutes)
  if (secondsDiff < TIME.HOUR) {
    return timeFormats[style][ETU.MINUTE](date)
  }

  // under a day (so display hours)
  if (secondsDiff < TIME.DAY) {
    return timeFormats[style][ETU.HOUR](date)
  }

  // under a month (so display days)
  if (secondsDiff < TIME.MONTH) {
    return timeFormats[style][ETU.DAY](date)
  }

  // over a month, same year (so display short date)
  if (isThisYear(date)) {
    return timeFormats[style][ETU.SAME_YEAR](date)
  }

  // over a month, different year (so display short date with year)
  if (isThisYear(date) === false) {
    return timeFormats[style][ETU.DIFF_YEAR](date)
  }
}
